import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { BehaviorSubject } from 'rxjs';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { ObjectTypes } from '@fi-shared/types';
import { ILightWeightUser } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

export enum FiReadsUserType {
    ShownBy = 0,
    LikedBy = 1,
}

interface IUserInteractionsDialogData {
    objectId: number;
    objectType: ObjectTypes;
    readsUserType: FiReadsUserType;
    usersData: {
        UserId: number;
        UserProfile: IUserProfile;
        UserBusinessUnitName: string;
        ObjectId: number;
        ObjectType: number;
        CategoryType?: number;
        UserFullName?: string;
    }[];
}
@Component({
    selector: 'fi-reads-user-list-data-dialog',
    template: `
        @if (!(loading$ | async)) {
          <div class="fi-reads-user-list-data-dialog-container">
            <mat-tab-group [(selectedIndex)]="selectedIndex" [mat-stretch-tabs]="false">
              <mat-tab>
                <ng-template mat-tab-label>{{ tab1Label }}</ng-template>
                <fi-users-list-related-to-object-type [users$]="shownByUserData$">
                </fi-users-list-related-to-object-type>
              </mat-tab>
              @if (isNotShownByAvailable()) {
                <mat-tab>
                  <ng-template mat-tab-label>{{ tab2Label }}</ng-template>
                  <fi-users-list-related-to-object-type [users$]="notShownByUserData$">
                  </fi-users-list-related-to-object-type>
                </mat-tab>
              }
            </mat-tab-group>
            <div class="fi-reads-user-list-data-dialog-actions">
              <button mat-icon-button mat-dialog-close>
                <span class="fas fa-times fa-lg"></span>
              </button>
            </div>
          </div>
        }
        @if (loading$ | async) {
          <div class="text-center">
            <fi-loading class="small"></fi-loading>
          </div>
        }
        `,
    styleUrls: ['./fi-reads-user-list-data-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-reads-user-list-data-dialog' },
    standalone: false,
})
export class FiUserInteractionsDialogComponent
    extends FiDialogsContentBase<IUserInteractionsDialogData, undefined>
    implements OnInit
{
    private appTextService: AppTextService;
    private newsFeedSharedService: FiNewsFeedSharedService;
    private userDataService: FiUserDataService;
    loading$ = new BehaviorSubject<boolean>(false);
    selectedIndex: 0;
    tab1Label: string;
    tab2Label: string;
    shownByUserData$ = new BehaviorSubject<ILightWeightUser[]>([]);
    notShownByUserData$ = new BehaviorSubject<ILightWeightUser[]>([]);

    ngOnInit(): void {
        this.newsFeedSharedService = this.injector.get(FiNewsFeedSharedService);
        this.appTextService = this.injector.get(AppTextService);
        this.userDataService = this.injector.get(FiUserDataService);

        this.loading$.next(true);
        this.init().finally(() => this.loading$.next(false));
    }

    private init() {
        return new Promise<void>((resolve, reject) => {
            if (this.data.readsUserType === FiReadsUserType.ShownBy) {
                this.newsFeedSharedService
                    .getUsersWithNewsFeedAccess(this.data.objectId, this.data.objectType)
                    .then((users) => {
                        const shownByUserIds = this.data.usersData.map((u) => u.UserId);
                        this.shownByUserData$.next(users.filter((u) => shownByUserIds.indexOf(u.Id) !== -1));
                        this.notShownByUserData$.next(
                            users.filter((u) => {
                                // remove current user from not shown by
                                return (
                                    shownByUserIds.indexOf(u.Id) === -1 &&
                                    (this.data.readsUserType !== FiReadsUserType.ShownBy ||
                                        u.Id !== this.userDataService.getUserId())
                                );
                            }),
                        );

                        this.tab1Label = this.appTextService.get('common.shown_by', {
                            '{0}': this.shownByUserData$.value.length,
                        });
                        this.tab2Label = this.appTextService.get('common.not_shown_by', {
                            '{0}': this.notShownByUserData$.value.length,
                        });

                        resolve();
                    }, reject);
            } else if (this.data.readsUserType === FiReadsUserType.LikedBy) {
                const shownByUserData: ILightWeightUser[] = this.data.usersData.map((userItem) => {
                    return {
                        Id: userItem.UserId,
                        FullName: userItem.UserFullName,
                        MediaBankPhysicalFileName: userItem.UserProfile.MediaBankPhysicalFileName,
                        MediaBankExtension: userItem.UserProfile.MediaBankExtension,
                        PreferredBusinessUnitName: userItem.UserBusinessUnitName,
                    };
                });
                this.shownByUserData$.next(shownByUserData);

                this.tab1Label = this.appTextService.get('common.liked_by', {
                    '{0}': this.shownByUserData$.value.length,
                });
                resolve();
            } else {
                reject();
            }
        });
    }

    isNotShownByAvailable() {
        return this.data.readsUserType === FiReadsUserType.ShownBy;
    }
}
